import { CLASSES, SIX_CLASSES, STARTER_TYPES } from './constants';
import type { AgeTier, StarterType } from '../types';

export interface Discipline {
  /** Value for `Disziplin`; for teams it must match the teammeldung.xsd enum. */
  readonly de: string;
  /** Value for `DisziplinEnglisch`. */
  readonly en: string;
  readonly cls: string;
}

/**
 * The disciplines available for a starter type.
 *
 * teammeldung.xsd enumerates 2er/4er/6er only, so the strings for those types
 * are assembled to match it exactly. 1er never appears in a Teammeldung, but
 * kunstradmeldung.xsd takes `Disziplin` as free text, so the same pattern is
 * used there for consistency.
 */
export function disciplinesFor(type: StarterType): readonly Discipline[] {
  const info = STARTER_TYPES[type];
  const classes = type === 'team6' ? CLASSES.filter((c) => SIX_CLASSES.includes(c[0])) : CLASSES;
  return classes.map(([de, en]) => ({
    de: `${info.prefix} ${de}`,
    en: `${info.prefixEn} ${en}`,
    cls: de,
  }));
}

/**
 * Reverse of `disciplinesFor()`, for importing a WeBo `Disziplin` string back
 * into a `StarterType` + class. Exact-matches against every type's `.de`
 * (covers teammeldung's closed enum and kunstradmeldung's free text, which in
 * practice uses the same strings) — `null` if nothing matches, e.g. a
 * hand-typed or foreign discipline text, or teammeldung's Einradsport
 * variants (there is no corresponding `StarterType` for those at all). The
 * literal text is never invented here; on a `null`, the caller keeps the raw
 * string and falls back to inferring `type` some other way (e.g. rider count).
 */
export function reverseDiscipline(discipline: string): { type: StarterType; cls: string } | null {
  for (const type of Object.keys(STARTER_TYPES) as StarterType[]) {
    const match = disciplinesFor(type).find((d) => d.de === discipline);
    if (match) return { type, cls: match.cls };
  }
  return null;
}

/** A women's class implies `GeschlechtStarter` = w. */
export function impliedSex(discipline: string): 'w' | 'm' | null {
  if (/Frauen|Juniorinnen|Schülerinnen/.test(discipline)) return 'w';
  return null;
}

/**
 * UCI 8.2.002 covers Elite and Junior together; 8.2.003 covers Youth
 * (Schüler) with lower limits. Derived from the class embedded in
 * `Starter.discipline` (see `disciplinesFor`) to match a starter against
 * programmes of the same tier when building an Entry — `Programme.ageTier`
 * itself is picked explicitly, not derived, since a programme exists
 * independently of any starter.
 */
export function ageTier(discipline: string): AgeTier {
  return /Schüler/.test(discipline) ? 'youth' : 'eliteJunior';
}
